import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { DeleteIcon, WarningIcon, InfoIcon, CheckCircleIcon, CloseIcon } from '@/components/common/icons';

// Colors
const colors = {
  primary: '#2563EB',
  primaryLight: '#3B82F6',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  errorDark: '#DC2626',
  info: '#06B6D4',
  infoLight: '#CFFAFE',
  gray50: '#F9FAFB',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
};

export type ConfirmationDialogType = 'delete' | 'warning' | 'info' | 'success';

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string | React.ReactNode;
  subMessage?: string;
  type?: ConfirmationDialogType;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  disableBackdropClick?: boolean;
}

const getTypeConfig = (type: ConfirmationDialogType) => {
  switch (type) {
    case 'delete':
      return {
        icon: <DeleteIcon sx={{ color: 'white', fontSize: 20 }} />,
        gradient: 'linear-gradient(135deg, #DC2626 0%, #EF4444 50%, #F87171 100%)',
        buttonColor: colors.error,
        buttonHoverColor: colors.errorDark,
      };
    case 'warning':
      return {
        icon: <WarningIcon sx={{ color: 'white', fontSize: 20 }} />,
        gradient: 'linear-gradient(135deg, #D97706 0%, #F59E0B 50%, #FBBF24 100%)',
        buttonColor: colors.warning,
        buttonHoverColor: '#B45309',
      };
    case 'success':
      return {
        icon: <CheckCircleIcon sx={{ color: 'white', fontSize: 20 }} />,
        gradient: 'linear-gradient(135deg, #059669 0%, #10B981 50%, #34D399 100%)',
        buttonColor: colors.success,
        buttonHoverColor: '#059669',
      };
    case 'info':
    default:
      return {
        icon: <InfoIcon sx={{ color: 'white', fontSize: 20 }} />,
        gradient: 'linear-gradient(135deg, #0891B2 0%, #06B6D4 50%, #22D3EE 100%)',
        buttonColor: colors.info,
        buttonHoverColor: '#0891B2',
      };
  }
};

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  subMessage,
  type = 'delete',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  disableBackdropClick = true,
}) => {
  const config = getTypeConfig(type);

  const handleDialogClose = (_event: object, reason: string) => {
    if (disableBackdropClick && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    if (!isLoading) {
      onClose();
    }
  };

  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={isLoading || disableBackdropClick}
      PaperProps={{
        sx: { borderRadius: 2, overflow: 'hidden' },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: colors.gray50,
          px: 3,
          py: 2,
          position: 'relative',
          borderBottom: `1px solid ${colors.gray200}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: config.buttonColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {config.icon}
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color={colors.gray600} sx={{ fontSize: '1rem' }}>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.gray500, fontSize: '0.7rem' }}>
              {type === 'delete' ? 'This action cannot be undone' : 'Please confirm your action'}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          disabled={isLoading}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: colors.gray500,
            '&:hover': { bgcolor: colors.gray200 },
            '&.Mui-disabled': { color: colors.gray300 },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ bgcolor: colors.gray50, pt: 3 }}>
        {typeof message === 'string' ? (
          <Typography variant="body2" color={colors.gray600}>
            {message}
          </Typography>
        ) : (
          message
        )}
        {subMessage && (
          <Typography
            variant="caption"
            color={colors.gray500}
            sx={{ display: 'block', mt: 1.5 }}
          >
            {subMessage}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, bgcolor: colors.gray50 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isLoading}
          sx={{
            textTransform: 'none',
            borderRadius: 1.5,
            fontWeight: 600,
            fontSize: '0.8rem',
            color: colors.gray600,
            borderColor: colors.gray300,
            '&:hover': { borderColor: colors.gray400, bgcolor: 'white' },
            '&.Mui-disabled': { borderColor: colors.gray200, color: colors.gray400 },
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading}
          sx={{
            textTransform: 'none',
            borderRadius: 1.5,
            fontWeight: 600,
            fontSize: '0.8rem',
            bgcolor: config.buttonColor,
            '&:hover': { bgcolor: config.buttonHoverColor },
            minWidth: 100,
          }}
        >
          {isLoading ? <CircularProgress size={20} color="inherit" /> : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
